/**
 * Checkout Configuration
 *
 * Configuration constants for checkout page redirection and URL parameters.
 * For utility functions, see @/utils/checkout/checkoutUtils
 *
 * NOTE: Availability codes and success codes have been moved to:
 * @/constants/davinci/returnCodes.ts - the single source of truth for all DaVinci codes
 * Use isAvailableCode() for availability checks and BOOKING_SUCCESS_CODES for success codes.
 */

export const CHECKOUT_CONFIG = {
  BASE_URL: '/buchung/buchen',

  // Query parameter names used in checkout URL
  QUERY_PARAMS: {
    OFFER_IDS: 'offerIds', // e.g., "123" (single) or "15-1" (multi)
    ROOM_CODES: 'roomCodes', // e.g., "J02" (single) or "D03-D01" (multi)
    SERVICE_VARIANT_IDS: 'serviceVariantIds', // e.g., "456" (single) or "789-790" (multi)
    PRODUCT_CODE: 'productCode', // Hotel/product GIATA code
    BOARD_CODE: 'boardCode', // Meal plan (unified across all rooms)
    CHECK_IN: 'checkIn', // Check-in date (YYYY-MM-DD)
    CHECK_OUT: 'checkOut', // Check-out date (YYYY-MM-DD)
    PAX: 'pax', // Passenger string (e.g., "r35-35" single, "r35-35r10-7" multi)
  },

  // Redirect URLs after booking submission
  REDIRECT_URLS: {
    THANK_YOU: '/buchung/buchung-erfolgreich', // Successful B or BC action (code 200)
    REQUEST_THANK_YOU: '/buchung/anfrage-erfolgreich', // Successful BQ action (code 206)
    DEAD_END: '/buchung/fehler', // Any booking failure
  },

  // Payment configuration
  PAYMENT: {
    DEFAULT_TYPE: 8, // Bank transfer
  },
} as const;

/**
 * ⚠️ TEMPORARY — hides the entire auxiliaries step (hotel extras + insurance).
 *
 * Due to unresolved legal issues with auxiliaries/insurances at launch, the whole
 * "Your Vamos journey" step (checkout activeStep 0, both sub-steps) is hidden:
 * the checkout opens directly on data entry and the progress bar shows 2 steps.
 *
 * To restore the auxiliaries step, set this to `false`.
 */
export const HIDE_AUXILIARIES_STEP = true;

/**
 * Rebate displays in the checkout: the rebate lines under each room, the
 * order-summary badge and struck-through original price, and the rebate rows in
 * the payment-step price table. When hidden, each room's rebate is folded into its
 * room price, so the line items still add up to the total.
 *
 * Re-enabled with VAM-304 once Vamos maintained rebates in DaVinci. Set to `true`
 * to hide them again.
 */
export const HIDE_REBATES_CHECKOUT = false;

/**
 * ⚠️ TEMPORARY — hides the rebate badges in the listing: SRP teasers and PDP room
 * offers.
 *
 * Whether and how rebates are shown there is still open with Vamos (VAM-305).
 * To restore them, set this to `false`.
 */
export const HIDE_REBATES_LISTING = true;
